import CaretPosition from 'textarea-caret-position'

/**
 * Service for managing loading indicators at cursor position in textarea
 */
class LoadingIndicatorService {
  private activeIndicators: Map<string, HTMLElement> = new Map()

  /**
   * Show a loading indicator at the cursor position in a textarea
   * @param textarea The textarea element
   * @param position The cursor position
   * @param id Unique identifier for this indicator
   * @returns The created indicator element or null if failed
   */
  showLoadingIndicator(
    textarea: HTMLTextAreaElement,
    position?: number,
    id: string = 'default'
  ): HTMLElement | null {
    // Remove existing indicator with same ID
    this.hideLoadingIndicator(id)

    const cursorPos = position ?? textarea.selectionStart

    // Hide cursor during loading if at cursor position
    if (!position || position === textarea.selectionStart) {
      textarea.style.caretColor = 'transparent'
    }

    try {
      const rect = textarea.getBoundingClientRect()
      const computed = window.getComputedStyle(textarea)

      // Use textarea-caret-position library for accurate position
      const caretPositionHelper = new CaretPosition(textarea)
      const caretPos = caretPositionHelper.get(cursorPos, cursorPos)

      // Calculate absolute position
      const x = rect.left + caretPos.left - textarea.scrollLeft
      const y = rect.top + caretPos.top - textarea.scrollTop

      // Get line height for vertical centering
      const lineHeight = caretPos.height || parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.5

      // Position indicator right at cursor with slight offset for visibility
      const finalX = Math.min(x + 2, rect.right - 50) // Small 2px offset for visibility
      // Position indicator at the vertical center of the current line
      const finalY = Math.max(rect.top, Math.min(y, rect.bottom - 30)) // Center vertically on line

      const indicator = document.createElement('div')
      indicator.className = 'continuation-loading-indicator'
      indicator.style.cssText = `
        position: fixed;
        left: ${finalX}px;
        top: ${finalY}px;
        z-index: 10000;
        pointer-events: none;
      `
      indicator.innerHTML = `
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      `
      document.body.appendChild(indicator)
      this.activeIndicators.set(id, indicator)

      return indicator
    } catch (error) {
      console.error('Error creating loading indicator:', error)
      return this.showFallbackIndicator(textarea, cursorPos, id)
    }
  }

  /**
   * Show a fallback loading indicator if the primary method fails
   */
  private showFallbackIndicator(
    textarea: HTMLTextAreaElement,
    cursorPos: number,
    id: string
  ): HTMLElement | null {
    try {
      const rect = textarea.getBoundingClientRect()
      const indicator = document.createElement('div')
      indicator.className = 'continuation-loading-indicator'
      indicator.style.cssText = `
        position: fixed;
        left: ${rect.left + 20}px;
        top: ${rect.top + rect.height - 40}px;
        z-index: 10000;
        pointer-events: none;
      `
      indicator.innerHTML = `
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      `
      document.body.appendChild(indicator)
      this.activeIndicators.set(id, indicator)
      return indicator
    } catch (error) {
      console.error('Loading indicator fallback also failed:', error)
      return null
    }
  }

  /**
   * Hide a loading indicator by ID
   * @param id The indicator ID to hide
   */
  hideLoadingIndicator(id: string = 'default') {
    const indicator = this.activeIndicators.get(id)
    if (indicator && indicator.parentNode) {
      indicator.parentNode.removeChild(indicator)
      this.activeIndicators.delete(id)
    }

    // Restore cursor visibility if no more indicators
    if (this.activeIndicators.size === 0) {
      const textarea = document.querySelector('textarea') as HTMLTextAreaElement
      if (textarea) {
        textarea.style.caretColor = ''
      }
    }
  }

  /**
   * Hide all active loading indicators
   */
  hideAllLoadingIndicators() {
    this.activeIndicators.forEach((indicator, id) => {
      if (indicator && indicator.parentNode) {
        indicator.parentNode.removeChild(indicator)
      }
    })
    this.activeIndicators.clear()

    // Restore cursor visibility
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement
    if (textarea) {
      textarea.style.caretColor = ''
    }
  }

  /**
   * Convenience methods for specific use cases
   */
  showContinuationLoading(textarea: HTMLTextAreaElement): { cursorPos: number; indicator: HTMLElement | null } {
    const cursorPos = textarea.selectionStart
    const indicator = this.showLoadingIndicator(textarea, cursorPos, 'continuation')
    return { cursorPos, indicator }
  }

  hideContinuationLoading(indicator: HTMLElement | null) {
    this.hideLoadingIndicator('continuation')
  }

  showGenerationLoading(textarea: HTMLTextAreaElement, position: number): HTMLElement | null {
    return this.showLoadingIndicator(textarea, position, 'generation')
  }

  hideGenerationLoading(indicator: HTMLElement | null) {
    this.hideLoadingIndicator('generation')
  }
}

// Export singleton instance
export const loadingIndicatorService = new LoadingIndicatorService()

// Export class for testing
export { LoadingIndicatorService }